import { History } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import AuthNavbar from "./AuthNavbar";

import ORLogo from "@/assets/OR_new_logo.png";
import ORLogoLight from "@/assets/OR_new_logo_light.png";
import Icons from "@/components/icons";
import { Button } from "@/components/ui/v2/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/v2/card";
import SearchInput from "@/components/ui/v2/search-input";
import { useLastUsedTenant } from "@/features/auth/hooks/useLastUsedTenant";
import { ThemeBackdrop } from "@/themes/theme-backdrop";
import { GetTenantsResponse } from "@/types/response/GetTenantsResponse";
import { TenantType } from "@/types/tenant/TenantType";

type SelectTenantProps = {
  tenants: GetTenantsResponse | undefined;
  setTenantId: (id: string) => void;
};

function matchesSearch(tenant: TenantType, search: string): boolean {
  if (!search.trim()) return true;
  const q = search.trim().toLowerCase();
  return tenant.name.toLowerCase().includes(q);
}

function TenantButton({
  tenant,
  isLastUsed,
  onSelect,
}: {
  tenant: TenantType;
  isLastUsed: boolean;
  onSelect: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <Button
      key={tenant.id}
      variant="outline"
      size="lg"
      className={`w-full justify-start h-auto py-4 px-4 bg-background-alt ${isLastUsed ? "ring-2 ring-primary/30 border-primary/50" : ""}`}
      onClick={() => onSelect(tenant.id)}
    >
      <Icons.DomainIcon className="size-5 shrink-0 text-muted-foreground" />
      <span className="text-base font-medium flex-1 text-left">
        {tenant.name}
      </span>
      {isLastUsed && (
        <span className="text-xs font-normal text-muted-foreground bg-muted/80 px-2 py-0.5 rounded-full shrink-0">
          {t("common.lastUsedTenant")}
        </span>
      )}
    </Button>
  );
}

export default function SelectTenant({
  tenants,
  setTenantId,
}: SelectTenantProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const { lastUsedTenantId, setLastUsedTenant } = useLastUsedTenant();

  const filteredTenants = useMemo(() => {
    if (!tenants?.data) return [];
    return tenants.data.filter((tenant) => matchesSearch(tenant, search));
  }, [tenants?.data, search]);

  const { lastUsed, others } = useMemo(() => {
    if (!lastUsedTenantId) {
      return { lastUsed: null, others: filteredTenants };
    }
    const last = filteredTenants.find((t) => t.id === lastUsedTenantId) ?? null;
    const rest = filteredTenants.filter((t) => t.id !== lastUsedTenantId);
    return { lastUsed: last, others: rest };
  }, [filteredTenants, lastUsedTenantId]);

  const handleSelectTenant = (id: string) => {
    setLastUsedTenant(id);
    setTenantId(id);
  };

  return (
    <div data-slot="auth-layout" className="relative flex flex-col items-center justify-center min-h-screen bg-background-alt">
      <ThemeBackdrop slot="auth" />
      <nav className="absolute top-0 right-0 p-4 z-10">
        <AuthNavbar />
      </nav>
      <div className="relative flex flex-col items-center justify-center gap-12 w-full px-4 py-8">
        <div className="flex items-center">
          <img src={ORLogoLight} alt="Brand Logo" className="w-[246px] h-14 dark:hidden" />
          <img src={ORLogo} alt="Brand Logo" className="hidden dark:block w-[246px] h-14" />
        </div>
        <Card className="w-full max-w-lg shadow-lg bg-background-alt">
          <CardHeader className="space-y-2 pb-4">
            <CardTitle className="text-2xl font-semibold tracking-tight">
              {t("common.selectTenant")}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-4">
            {lastUsed && (
              <div className="space-y-2 pb-4">
                <div
                  className="flex items-center gap-1.5 px-1 text-muted-foreground"
                  aria-label={t("common.lastUsedTenant")}
                >
                  <History className="size-4" aria-hidden />
                  <span className="text-xs font-medium uppercase tracking-wider">
                    {t("common.lastUsedTenant")}
                  </span>
                </div>
                <TenantButton
                  tenant={lastUsed}
                  isLastUsed
                  onSelect={handleSelectTenant}
                />
              </div>
            )}
            <SearchInput
              placeholder={t("common.searchTenants")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full"
            />
            <div className="flex flex-col gap-3 max-h-[380px] overflow-y-auto pr-3">
              {lastUsed && others.length > 0 && (
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-1">
                  {t("common.allTenants")}
                </p>
              )}
              {!lastUsed && filteredTenants.length > 0 && (
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-1">
                  {t("common.allTenants")}
                </p>
              )}
              {(lastUsed ? others : filteredTenants).map((tenant) => (
                <TenantButton
                  key={tenant.id}
                  tenant={tenant}
                  isLastUsed={false}
                  onSelect={handleSelectTenant}
                />
              ))}
              {filteredTenants.length === 0 && (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  {t("common.noTenantsMatch")}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
